import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock3,
  MessageSquare,
  Send,
  Sparkles,
} from "lucide-react";
import styled, { createGlobalStyle } from "styled-components";
import { MARKETPLACE_FONTS } from "./MarketplaceConstants";

const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
`;

const Page = styled.main`
  min-height: 100vh;
  background: #0d1012;
  color: var(--mp-headingColor);
  font-family: "Inter", ${MARKETPLACE_FONTS.SANS};
  padding: 28px 0 64px;

  [data-marketplace-theme="light"] & {
    background: #f6f7f4;
  }
`;

const Container = styled.div`
  width: min(920px, calc(100% - 48px));
  margin: 0 auto;

  @media (max-width: 720px) {
    width: min(100% - 32px, 920px);
  }
`;

const BackButton = styled.button`
  border: 1px solid var(--mp-sidebarBorder);
  border-radius: 999px;
  background: var(--mp-cardBackground);
  color: var(--mp-headingColor);
  min-height: 30px;
  padding: 0 11px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font: 700 10px/14px "Inter", ${MARKETPLACE_FONTS.SANS};
  margin-bottom: 22px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 22px;

  @media (max-width: 820px) {
    flex-direction: column;
  }
`;

const Eyebrow = styled.div`
  color: var(--mp-heroSubColor);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const Title = styled.h1`
  margin: 0 0 12px;
  color: var(--mp-headingColor);
  font-family: "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  font-size: clamp(24px, 4vw, 34px);
  line-height: 1.08;
`;

const Summary = styled.p`
  max-width: 460px;
  margin: 0;
  color: var(--mp-subColor);
  font-size: 11px;
  line-height: 17px;
`;

const StatusPill = styled.div`
  border: 1px solid rgba(255, 222, 161, 0.28);
  border-radius: 999px;
  background: rgba(255, 222, 161, 0.14);
  color: #ffdea1;
  min-height: 28px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 800;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 176px;
  gap: 16px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border: 1px solid var(--mp-sidebarBorder);
  border-radius: 12px;
  background: var(--mp-cardBackground);
  padding: 18px;
`;

const PanelTitle = styled.h2`
  margin: 0 0 14px;
  color: var(--mp-headingColor);
  font-size: 15px;
  line-height: 20px;
`;

const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const ResourceCard = styled.div`
  min-height: 72px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--mp-cardBackground) 88%, var(--mp-headingColor));
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Swatch = styled.div<{ $accent?: string }>`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: ${p => p.$accent || "var(--mp-heroBtnBg)"};
  box-shadow: 0 12px 24px rgba(0,0,0,0.18);
`;

const ResourceTitle = styled.h3`
  margin: 0 0 4px;
  color: var(--mp-headingColor);
  font-size: 10px;
  line-height: 14px;
`;

const ResourceMeta = styled.p`
  margin: 0;
  color: var(--mp-subColor);
  font-size: 9px;
  line-height: 13px;
`;

const LineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LineItem = styled.div`
  min-height: 30px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--mp-cardBackground) 88%, var(--mp-headingColor));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px 6px 12px;
  color: var(--mp-headingColor);
  font-size: 10px;
  font-weight: 700;
`;

const Tag = styled.span`
  border-radius: 999px;
  background: rgba(255, 222, 161, 0.13);
  color: #ffdea1;
  padding: 4px 8px;
  font-size: 9px;
`;

const Notes = styled.p`
  margin: 0;
  color: var(--mp-subColor);
  font-size: 11px;
  line-height: 18px;
`;

const Side = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QuoteSummary = styled(Panel)`
  position: sticky;
  top: 96px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: var(--mp-subColor);
  font-size: 10px;
  margin-bottom: 9px;

  strong {
    color: var(--mp-headingColor);
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  min-height: 34px;
  border: 0;
  border-radius: 999px;
  background: var(--mp-heroBtnBg);
  color: #24180c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font: 800 10px/14px "Inter", ${MARKETPLACE_FONTS.SANS};
  margin-top: 10px;
`;

const SecondaryButton = styled(PrimaryButton)`
  background: transparent;
  color: var(--mp-headingColor);
  border: 1px solid var(--mp-sidebarBorder);
`;

const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const resources = [
  { title: "ARRI Alexa Mini LF Package", meta: "Camera body, media, operator kit", accent: "linear-gradient(135deg, #2ec4b6, #0b7285)" },
  { title: "Aputure Nova Lighting Grid", meta: "High-output daylight LED fixtures", accent: "linear-gradient(135deg, #ffe066, #f76707)" },
  { title: "Director of Photography", meta: "Lead camera and look supervision", accent: "linear-gradient(135deg, #ff8cc6, #c94daa)" },
  { title: "Key Grip Team", meta: "Rigging, movement, safety support", accent: "linear-gradient(135deg, #8ce99a, #2b8a3e)" },
];

const crew = [
  "Director of Photography",
  "1st AC / Focus Puller",
  "Gaffer",
  "Key Grip",
];

const MarketplaceProjectQuotePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Page>
      <Fonts />
      <Container>
        <BackButton onClick={() => navigate("/marketplace/dashboard")}>
          <ArrowLeft size={15} />
          Back to dashboard
        </BackButton>

        <Header>
          <div>
            <Eyebrow><Sparkles size={14} /> Quote request KMQ-8029</Eyebrow>
            <Title>Neon Horizon Campaign</Title>
            <Summary>
              Comprehensive production package including specialty camera systems,
              a custom lighting grid, and full crew support for a three-day night
              shoot downtown.
            </Summary>
          </div>
          <StatusPill><Clock3 size={14} /> Under Review</StatusPill>
        </Header>

        <Layout>
          <SectionStack>
            <Panel>
              <PanelTitle>Requested Resources</PanelTitle>
              <ResourceGrid>
                {resources.map(item => (
                  <ResourceCard key={item.title}>
                    <Swatch $accent={item.accent} />
                    <div>
                      <ResourceTitle>{item.title}</ResourceTitle>
                      <ResourceMeta>{item.meta}</ResourceMeta>
                    </div>
                  </ResourceCard>
                ))}
              </ResourceGrid>
            </Panel>

            <Panel>
              <PanelTitle>Crew & Talent</PanelTitle>
              <LineList>
                {crew.map((item, index) => (
                  <LineItem key={item}>
                    {item}
                    <Tag>{index === 0 ? "3 days" : "1 team"}</Tag>
                  </LineItem>
                ))}
              </LineList>
            </Panel>

            <Panel>
              <PanelTitle>Project Notes</PanelTitle>
              <Notes>
                This shoot requires extensive night-time exterior setups. Power
                distribution is critical, and the camera department needs a compact
                high-speed package with wireless monitoring. The production expects
                a polished commercial look with reflective neon color, controlled
                haze, and fast company moves between locations.
              </Notes>
            </Panel>
          </SectionStack>

          <Side>
            <QuoteSummary>
              <PanelTitle>Quote Summary</PanelTitle>
              <SummaryRow><span>Timeline</span><strong>Nov 14 - Nov 16</strong></SummaryRow>
              <SummaryRow><span>Location</span><strong>Downtown LA</strong></SummaryRow>
              <SummaryRow><span>Target Budget</span><strong>$48k - $60k</strong></SummaryRow>
              <SummaryRow><span>Resources</span><strong>8 items</strong></SummaryRow>
              <PrimaryButton><MessageSquare size={15} /> Message Support</PrimaryButton>
              <SecondaryButton><Send size={15} /> Bid Request</SecondaryButton>
            </QuoteSummary>

            <Panel>
              <PanelTitle>Milestones</PanelTitle>
              <LineList>
                <LineItem><CheckCircle2 size={15} /> Brief received</LineItem>
                <LineItem><CalendarDays size={15} /> Vendor matching</LineItem>
                <LineItem><Clock3 size={15} /> Quote finalization</LineItem>
              </LineList>
            </Panel>
          </Side>
        </Layout>
      </Container>
    </Page>
  );
};

export default MarketplaceProjectQuotePage;
